import { useState, useId, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Download, FileDown, Table2, MapPin } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import toast from "react-hot-toast";
import { fetchEntity, fetchChangelog, fetchCountries, patchCountryProviders, reassignEntityCountry, type CountryProvidersPatch } from "../lib/dataFetchers";
import { api, extractApiError, apiBaseUrl } from "../lib/api";
import { cn } from "../lib/cn";
import { useSession } from "../lib/auth";
import { canEdit as canEditRole, canDownload } from "../lib/permissions";
import { useEntitySections, FIELD_LABELS, type Country, type Entity } from "../lib/types";
import { InlineEdit } from "../components/InlineEdit";
import { AddressField } from "../components/AddressField";
import { SelectOrOtherEdit } from "../components/SelectOrOtherEdit";
import { ObligationEditor } from "../components/ObligationEditor";
import { OwnershipEditor } from "../components/OwnershipEditor";
import { DirectorsEditor } from "../components/DirectorsEditor";
import { TypedFieldsEditor } from "../components/TypedFieldsEditor";
import { DeleteEntityDialog } from "../components/DeleteEntityDialog";
import { EntityRenewalsMatrix } from "../components/EntityRenewalsMatrix";
import { DocumentsSection } from "../components/documents/DocumentsSection";
import { ChangelogTimeline } from "../components/ChangelogTimeline";
import { HistoryTimelineErrorBoundary } from "../components/HistoryTimelineErrorBoundary";
import { Term } from "../components/ui/Term";
import { DataAuditWarning } from "../components/DataAuditWarning";
import { SectionCard } from "../components/ui/SectionCard";
import { Button } from "../components/ui/Button";
import { EntityHistoryFieldsProvider } from "../lib/EntityHistoryFieldsContext";


// ─── Country provider field definitions ──────────────────────────────────────

type ProviderFieldKey = keyof Omit<CountryProvidersPatch, "comment">;

const PROVIDER_FIELDS: ProviderFieldKey[] = [
  "lawyerTaxCorporate",
  "lawyerLabourHr",
  "litigationLawyer",
  "accountant",
  "statutoryAuditor",
  "banker",
  "debtCollectionAgency",
  "otherServiceProvider",
  "visaProvider",
  "healthcareProvider",
  "benefitsProvider",
  "payrollProvider",
  "taxGuideFirm",
  "taxGuideLastUpdated",
];

// ─── Country as a normal field row ───────────────────────────────────────────
// Per DLD §D: remove the always-visible CountryReassignRow dropdown.
// Country renders as a standard field row. Pencil → inline searchable combobox.

interface CountryFieldProps {
  entityId: string;
  currentCountryId: string;
  currentCountry: Country | undefined;
  currentRegion: string;
  canEdit: boolean;
}

function CountryField({
  entityId,
  currentCountryId,
  currentCountry,
  currentRegion,
  canEdit,
}: CountryFieldProps): JSX.Element {
  const { t } = useTranslation("entities");
  const { t: tCommon } = useTranslation("common");
  const qc = useQueryClient();
  const [pickingCountry, setPickingCountry] = useState(false);
  const [selectedId, setSelectedId] = useState(currentCountryId);
  const [comment, setComment] = useState("");
  const [countrySearch, setCountrySearch] = useState("");

  useEffect(() => {
    if (!pickingCountry) setSelectedId(currentCountryId);
  }, [currentCountryId, pickingCountry]);

  const { data: countries = [] } = useQuery({
    queryKey: ["countries"],
    queryFn: fetchCountries,
    staleTime: 5 * 60 * 1000,
  });

  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries.slice(0, 20);
    const sq = countrySearch.toLowerCase();
    return countries.filter((c) => c.name.toLowerCase().includes(sq)).slice(0, 20);
  }, [countries, countrySearch]);

  const mutation = useMutation({
    mutationFn: () => reassignEntityCountry(entityId, selectedId, comment.trim() || undefined),
    onSuccess: () => {
      toast.success(t("country_reassign_success"));
      setPickingCountry(false);
      setComment("");
      setCountrySearch("");
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["entities-all"] });
      void qc.invalidateQueries({ queryKey: ["countries"] });
      void qc.invalidateQueries({ queryKey: ["changelog-recent"] });
      void qc.invalidateQueries({ queryKey: ["renewals"] });
      void qc.invalidateQueries({ queryKey: ["entity-renewal-status", entityId] });
    },
    onError: (err: unknown) => toast.error(extractApiError(err) || t("country_reassign_error")),
  });

  return (
    <>
      {/* Country row */}
      <div data-field="country" className="flex items-start gap-[14px] py-2 group">
        <span className="w-[148px] shrink-0 text-[13.5px] font-semibold text-text pt-1">
          {t("field_country_label_short")}
        </span>
        <div className="flex-1 min-w-0">
          {!pickingCountry ? (
            <div className="flex items-center gap-2 min-h-[34px]">
              <span className="text-[14px] font-normal text-text">
                {currentCountry ? (
                  <>
                    {currentCountry.flag && <span aria-hidden className="mr-1.5">{currentCountry.flag}</span>}
                    {currentCountry.name}
                  </>
                ) : <span className="text-dim">—</span>}
              </span>
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Edit ${t("field_country_label_short")}`}
                  onClick={() => setPickingCountry(true)}
                  className={cn(
                    "opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity",
                    "inline-flex items-center justify-center h-[28px] w-[28px] rounded-md",
                    "text-dim hover:text-accent",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
                    "min-h-[44px] sm:min-h-[28px]",
                  )}
                >
                  <svg className="h-[13px] w-[13px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M4 20h4l10-10-4-4L4 16z" />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2 py-1">
              {/* Searchable country combobox */}
              <div className="relative">
                <input
                  type="search"
                  placeholder={t("field_country_placeholder")}
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  className={cn(
                    "w-full h-9 px-3 rounded-lg border border-border bg-surface-2 text-base text-text",
                    "focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none",
                    "min-h-[44px] sm:min-h-[36px] sm:h-9",
                  )}
                  onKeyDown={(e) => { if (e.key === "Escape") { setPickingCountry(false); setCountrySearch(""); } }}
                />
                {filteredCountries.length > 0 && (
                  <ul className="absolute top-full left-0 mt-1 z-50 w-full bg-surface border border-border rounded-[10px] shadow-pop max-h-[200px] overflow-y-auto p-1">
                    {filteredCountries.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className={cn(
                            "w-full text-left px-3 py-2 rounded-[7px] text-[13px] hover:bg-surface-2 flex items-center gap-2",
                            selectedId === c.id && "bg-accent/10 text-accent",
                          )}
                          onClick={() => { setSelectedId(c.id); setCountrySearch(c.name); }}
                        >
                          {c.flag && <span aria-hidden>{c.flag}</span>}
                          <span className="truncate">{c.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("country_reassign_comment_placeholder")}
                aria-label={t("country_reassign_comment_aria")}
                className={cn(
                  "h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
                  "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                  "min-h-[44px] sm:min-h-[36px] sm:h-9",
                )}
                onKeyDown={(e) => {
                  if (e.key === "Escape") { setPickingCountry(false); setCountrySearch(""); }
                  if (e.key === "Enter") mutation.mutate();
                }}
              />
              <div className="flex gap-2">
                <Button variant="primary" disabled={mutation.isPending || selectedId === currentCountryId} onClick={() => mutation.mutate()}>
                  {mutation.isPending ? "…" : t("country_reassign_save")}
                </Button>
                <Button variant="secondary" onClick={() => { setPickingCountry(false); setCountrySearch(""); setComment(""); setSelectedId(currentCountryId); }}>
                  {t("country_reassign_cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Region row — always read-only, shows "(derived)" hint */}
      <div data-field="region" className="flex items-center gap-[14px] py-2">
        <span className="w-[148px] shrink-0 text-[13.5px] font-semibold text-text">
          {t("field_region_label")}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-[14px] font-normal text-text">
            {currentRegion
              ? tCommon(`region_${currentRegion}`, currentRegion)
              : <span className="text-dim font-normal">—</span>}
          </span>
          <span
            title={t("field_region_derived_hint")}
            aria-label={t("field_region_derived_hint")}
            className="text-[11px] text-dim cursor-default select-none"
          >
            ({t("field_region_derived")})
          </span>
        </div>
      </div>
    </>
  );
}

// ─── SelectOrOtherEdit wrapper that wires into InlineEdit's PATCH mutation ────

interface SelectOrOtherEditFieldProps {
  entityId: string;
  field: string;
  value: string | null;
  canEdit: boolean;
  options: { value: string; label: string }[];
  ariaLabel: string;
}

function SelectOrOtherEditField({
  entityId,
  field,
  value,
  canEdit,
  options,
  ariaLabel,
}: SelectOrOtherEditFieldProps): JSX.Element {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (newValue: string | null) => {
      const res = await api.patch(`/entities/${entityId}`, {
        field,
        value: newValue,
        comment: null,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success(`${field} updated`);
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["changelog-recent"] });
      void qc.invalidateQueries({ queryKey: ["renewals"] });
      void qc.invalidateQueries({ queryKey: ["changelog", { entityId }] });
      void qc.invalidateQueries({ queryKey: ["entity-renewal-status", entityId] });
    },
    onError: (err: unknown) => toast.error(extractApiError(err)),
  });

  return (
    <SelectOrOtherEdit
      value={value}
      options={options}
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onSave={(v) => mutation.mutate(v)}
      disabled={mutation.isPending}
    />
  );
}

// ─── Country Services & Tax Guide card ───────────────────────────────────────
// Per DLD: NOT a reusable component — inline only on EntityDetail.

interface CountryServicesCardProps {
  country: Country;
  entityId: string;
  canEdit: boolean;
}

function CountryServicesCard({ country, entityId, canEdit }: CountryServicesCardProps): JSX.Element {
  const { t } = useTranslation(["entities", "country"]);
  const qc = useQueryClient();
  const uid = useId();

  const [editingField, setEditingField] = useState<ProviderFieldKey | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const [editComment, setEditComment] = useState("");
  const commentId = `${uid}-country-comment`;

  const mutation = useMutation({
    mutationFn: ({ field, value }: { field: ProviderFieldKey; value: string }) =>
      patchCountryProviders(country.id, {
        [field]: value.trim() === "" ? null : value.trim(),
        comment: editComment.trim(),
      } as CountryProvidersPatch),
    onSuccess: () => {
      toast.success(t("country_services_save_success"));
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["entities-all"] });
      setEditingField(null);
      setDraftValue("");
      setEditComment("");
    },
    onError: (err) => toast.error(extractApiError(err) || t("country_services_save_error")),
  });

  const startEdit = (field: ProviderFieldKey) => {
    const currentVal = (country[field] as string | null | undefined) ?? "";
    setDraftValue(currentVal);
    setEditComment("");
    setEditingField(field);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setDraftValue("");
    setEditComment("");
  };

  const formatGeo = (n: number, decimals = 4) => n.toFixed(decimals);

  return (
    <SectionCard
      title={t("section_country_services")}
      hint={canEdit ? t("country_services_applies_to", { country: country.name }) : undefined}
    >
      {/* Geo metadata — read-only */}
      <div
        data-field="country.iso2"
        className="flex items-center gap-2 text-[12.5px] text-label mb-3 pb-3 border-b border-border-2"
        title={t("country:reference_tooltip")}
      >
        <MapPin className="h-3.5 w-3.5 shrink-0 text-dim" aria-hidden />
        <span>
          <span className="text-dim mr-1">{t("country:section_label")}:</span>
          <span className="font-medium">{country.iso2}</span>
          {" · "}
          <span data-field="country.lat" className="font-mono tabular-nums">
            {formatGeo(Math.abs(country.lat))}°{country.lat >= 0 ? "N" : "S"}
          </span>
          {", "}
          <span data-field="country.lng" className="font-mono tabular-nums">
            {formatGeo(Math.abs(country.lng))}°{country.lng >= 0 ? "E" : "W"}
          </span>
        </span>
      </div>

      <div className="divide-y divide-border-2">
        {PROVIDER_FIELDS.map((field) => {
          const currentVal = (country[field] as string | null | undefined) ?? null;
          const labelKey = `country_services_label_${field}` as const;
          const isEditing = editingField === field;

          return (
            <div
              key={field}
              data-field={`country.${field}`}
              className="flex items-start gap-[14px] py-2 group"
            >
              <span className="w-[148px] shrink-0 text-[13.5px] font-semibold text-text pt-1">
                {t(labelKey)}
              </span>
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <div className="space-y-2 py-1">
                    <input
                      type="text"
                      value={draftValue}
                      onChange={(e) => setDraftValue(e.target.value)}
                      placeholder={t(labelKey)}
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                      className={cn(
                        "h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
                        "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                      )}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                    <input
                      id={commentId}
                      type="text"
                      value={editComment}
                      onChange={(e) => setEditComment(e.target.value)}
                      placeholder={t("country_services_edit_comment")}
                      aria-label={t("country_services_comment_aria")}
                      className={cn(
                        "h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
                        "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                      )}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") cancelEdit();
                        if (e.key === "Enter" && editComment.trim()) {
                          mutation.mutate({ field, value: draftValue });
                        }
                      }}
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        disabled={mutation.isPending || !editComment.trim()}
                        onClick={() => mutation.mutate({ field, value: draftValue })}
                      >
                        {mutation.isPending ? "…" : t("country_services_save")}
                      </Button>
                      <Button variant="secondary" onClick={cancelEdit}>
                        {t("country_services_cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 min-h-[34px]">
                    {currentVal ? (
                      <span className="text-[14px] font-normal text-text">{currentVal}</span>
                    ) : (
                      <span className="text-[14px] text-dim font-normal">—</span>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => startEdit(field)}
                        aria-label={`Edit ${t(labelKey)}`}
                        className={cn(
                          "opacity-0 group-hover:opacity-100 focus:opacity-100",
                          "inline-flex items-center rounded px-1.5 py-0.5 text-[11px] text-dim",
                          "hover:text-accent focus:outline-none focus:ring-1 focus:ring-accent/30 min-h-[28px] transition-opacity",
                        )}
                      >
                        <svg className="h-[13px] w-[13px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M4 20h4l10-10-4-4L4 16z" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {country.taxGuideLastUpdated && (
        <p className="mt-3 text-[11px] text-dim border-t border-border-2 pt-2">
          {t("country_services_label_taxGuideLastUpdated")}: {country.taxGuideLastUpdated}
        </p>
      )}
    </SectionCard>
  );
}

export default function EntityDetail(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSession();
  const canEdit = canEditRole(user?.role);
  const role = user?.role ?? null;
  const { t } = useTranslation(["entities", "common", "audit", "cycles", "obligations"]);
  const entitySections = useEntitySections();
  const locale = i18n.language;

  const { data: entity, isLoading, isError } = useQuery({
    queryKey: ["entity", id],
    queryFn: () => fetchEntity(id),
    enabled: Boolean(id),
  });

  const [searchParams] = useSearchParams();
  const targetField = searchParams.get("field");

  useEffect(() => {
    if (!targetField || isLoading || !entity) return;
    const t2 = window.setTimeout(() => {
      const selector = `[data-field="${CSS.escape(targetField)}"]`;
      const target = document.querySelector(selector) as HTMLElement | null;
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add("ring-2", "ring-accent", "ring-offset-2", "rounded-md", "transition-all");
      window.setTimeout(() => {
        target.classList.remove("ring-2", "ring-accent", "ring-offset-2", "rounded-md");
      }, 2200);
    }, 100);
    return () => window.clearTimeout(t2);
  }, [targetField, isLoading, entity?.id]);

  // ── Per-entity Change history ─────────────────────────────────────────────
  const CHANGELOG_PAGE = 20;
  const [changelogLimit, setChangelogLimit] = useState(CHANGELOG_PAGE);
  const { data: changelogData, isLoading: changelogLoading } = useQuery({
    queryKey: ["changelog", { entityId: id, limit: changelogLimit, hideSystem: true }],
    queryFn: () => fetchChangelog({ entityId: id, limit: changelogLimit, hideSystem: true }),
    enabled: Boolean(id),
  });
  const changelogItems = useMemo(
    () => changelogData?.items ?? [],
    [changelogData],
  );
  const changelogTotal = changelogData?.total ?? changelogItems.length;
  const changelogHasMore = changelogItems.length < changelogTotal;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-3">
        <div className="skeleton h-8 w-1/2" />
        <div className="skeleton h-6 w-1/3" />
        <div className="skeleton h-48" />
      </div>
    );
  }

  if (isError || !entity) {
    return (
      <div className="max-w-2xl mx-auto bg-surface border border-border rounded-[11px] shadow-card p-6 text-center">
        <h1 className="font-display font-semibold text-[18px] text-text">{t("entities:entity_not_found")}</h1>
        <button
          type="button"
          onClick={() => navigate("/entities")}
          className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-[12.5px] hover:bg-surface-2 min-h-[44px] sm:min-h-[34px]"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> {t("entities:back_to_list")}
        </button>
      </div>
    );
  }

  return (
    <EntityHistoryFieldsProvider items={changelogItems}>
    <div className="max-w-4xl mx-auto">
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[12.5px] text-muted font-medium mb-3 hover:text-text min-h-[44px] sm:min-h-0"
      >
        <ArrowLeft className="h-[15px] w-[15px]" aria-hidden />
        {t("common:back")}
      </button>

      {/* ── Entity page header ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-[14px] mb-[18px] flex-wrap">
        <div className="flex items-center gap-[13px] min-w-0">
          {/* Country flag */}
          {entity.country?.iso2 ? (
            <img
              src={`https://flagcdn.com/${entity.country.iso2.toLowerCase()}.svg`}
              alt=""
              className="w-9 h-[26px] rounded-[4px] object-cover border border-black/8 shrink-0"
              aria-hidden
            />
          ) : (
            <span aria-hidden className="text-3xl shrink-0">{entity.country?.flag ?? "🌐"}</span>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display font-semibold text-[24px] leading-[1.1] text-text">
                {entity.name}
              </h1>
            </div>
            <p className="text-[13px] text-muted mt-[5px]">
              {entity.country?.name ?? "—"} · {entity.legalForm ?? "—"} · {entity.region}
            </p>
            <p
              data-testid="entity-timestamps"
              className="text-[11px] text-dim mt-0.5"
            >
              {t("entities:metadata_created_updated", {
                created: new Date(entity.createdAt).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" }),
                updated: new Date(entity.updatedAt).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" }),
              })}
            </p>
          </div>
        </div>

        {/* Download dropdown — gated to canDownload (all authenticated roles) */}
        {canDownload(user?.role) && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label={t("entities:download")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 h-[34px]",
                  "text-[12.5px] font-semibold text-text hover:bg-surface-2 hover:border-[#dcd9d2]",
                  "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
                  "min-h-[44px] sm:min-h-[34px]",
                )}
              >
                <Download className="h-4 w-4" aria-hidden />
                {t("entities:download")}
                <ChevronDown className="h-[13px] w-[13px] ml-0.5 text-dim" aria-hidden />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                sideOffset={6}
                align="end"
                className="z-50 min-w-[160px] bg-surface border border-border rounded-[10px] shadow-pop p-1.5 animate-in fade-in-0 zoom-in-95"
              >
                <DropdownMenu.Item asChild>
                  <a
                    href={`${apiBaseUrl}/entities/${entity.id}/export/docx`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-[10px] py-2 rounded-[7px] text-[13px] text-text cursor-pointer hover:bg-surface-2 focus:bg-surface-2 focus:outline-none min-h-[36px]"
                  >
                    <FileDown className="h-4 w-4 text-dim" aria-hidden />
                    {t("entities:download_document")}
                  </a>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <a
                    href={`${apiBaseUrl}/entities/${entity.id}/export/xlsx`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-[10px] py-2 rounded-[7px] text-[13px] text-text cursor-pointer hover:bg-surface-2 focus:bg-surface-2 focus:outline-none min-h-[36px]"
                  >
                    <Table2 className="h-4 w-4 text-dim" aria-hidden />
                    {t("entities:download_excel")}
                  </a>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>

      {/* ── Data integrity gaps ribbon ─────────────────────────────────── */}
      <DataAuditWarning gaps={entity.dataIntegrityGaps ?? []} />

      <div className="space-y-[14px]">
        {/* ── Renewals matrix — first card per CLAUDE.md Phase VV ── */}
        <EntityRenewalsMatrix entityId={id} canEdit={canEdit} />

        {/* ── Obligation editor — full-width on top ── */}
        <ObligationEditor
          entityId={id}
          canEdit={canEdit}
          entity={{
            incorporationDateTyped: entity.incorporationDateTyped ?? null,
            fiscalYearEndMonth: entity.fiscalYearEndMonth ?? null,
            fiscalYearEndDay: entity.fiscalYearEndDay ?? null,
            agmDate: entity.agmDate ?? null,
          }}
        />

        {/* ── Section cards — single column at all widths ── */}
        {/* Order: entity field sections (Legal Identity first via useEntitySections()),
            then Dates & Duration, Country Services, Documents, Delete entity */}
        <div className="space-y-[14px]">
          {entitySections.map((s) => {
            const isCapitalSection = s.sectionKey === "capital_ownership";
            const isDirectorsSection = s.sectionKey === "directors";
            const isNotesSection = s.sectionKey === "notes";
            const isAddressSection = s.sectionKey === "address";

            return (
              <div
                key={s.sectionKey}
              >
                <SectionCard title={s.title}>
                  {isAddressSection ? (
                    <div className="divide-y divide-border-2">
                      <AddressField
                        entity={entity}
                        prefix="operational"
                        label={t("entities:address_group_operational", { defaultValue: "Operational address" })}
                        canEdit={canEdit}
                      />
                      <AddressField
                        entity={entity}
                        prefix="registered"
                        label={t("entities:address_group_registered", { defaultValue: "Registered address" })}
                        canEdit={canEdit}
                      />
                      {/* Contact + physical office */}
                      <>
                        {(["contact", "physicalOfficeRequired", "physicalOfficeType"] as (keyof Entity)[]).map((f) => (
                          <div key={f as string} data-field={f as string} className="flex items-center gap-[14px] py-2 group">
                            <span className="w-[148px] shrink-0 text-[13.5px] font-semibold text-text">{FIELD_LABELS[f] ?? (f as string)}</span>
                            <div className="flex-1 min-w-0">
                              {f === "physicalOfficeRequired" ? (
                                <SelectOrOtherEditField
                                  entityId={entity.id}
                                  field="physicalOfficeRequired"
                                  value={(entity[f] as string | null) ?? null}
                                  canEdit={canEdit}
                                  options={[
                                    { value: "Yes", label: t("entities:physical_office_yes", { defaultValue: "Yes" }) },
                                    { value: "No", label: t("entities:physical_office_no", { defaultValue: "No" }) },
                                  ]}
                                  ariaLabel={FIELD_LABELS[f] ?? (f as string)}
                                />
                              ) : f === "physicalOfficeType" ? (
                                <SelectOrOtherEditField
                                  entityId={entity.id}
                                  field="physicalOfficeType"
                                  value={(entity[f] as string | null) ?? null}
                                  canEdit={canEdit}
                                  options={[
                                    { value: "Co-working — open space", label: t("entities:physical_office_type_cowork_open", { defaultValue: "Co-working — open space" }) },
                                    { value: "Co-working — closed office", label: t("entities:physical_office_type_cowork_closed", { defaultValue: "Co-working — closed office" }) },
                                    { value: "Regular office space", label: t("entities:physical_office_type_regular", { defaultValue: "Regular office space" }) },
                                  ]}
                                  ariaLabel={FIELD_LABELS[f] ?? (f as string)}
                                />
                              ) : (
                                <InlineEdit entityId={entity.id} field={f} value={(entity[f] as string | null) ?? null} canEdit={canEdit} role={role} />
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    </div>
                  ) : isDirectorsSection ? (
                    <DirectorsEditor entityId={entity.id} canEdit={canEdit} />
                  ) : isCapitalSection ? (
                    <div className="space-y-4">
                      <OwnershipEditor entityId={entity.id} canEdit={canEdit} />
                      {s.fields.length > 0 && (
                        <div className="border-t border-border-2 pt-3 divide-y divide-border-2">
                          {s.fields.map((f) => (
                            <div
                              key={f as string}
                              data-field={f as string}
                              className="flex items-center gap-[14px] py-2 group"
                            >
                              <span className="w-[148px] shrink-0 text-[13.5px] font-semibold text-text">
                                {(f === "ubo" || f === "uboDeclaration") ? (
                                  <Term term="ubo">{FIELD_LABELS[f] ?? (f as string)}</Term>
                                ) : (
                                  FIELD_LABELS[f] ?? (f as string)
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <InlineEdit
                                  entityId={entity.id}
                                  field={f}
                                  value={(entity[f] as string | null) ?? null}
                                  canEdit={canEdit}
                                  role={role}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Share capital + par value — typed money fields */}
                      <div className="border-t border-border-2 pt-3">
                        <TypedFieldsEditor entity={entity} canEdit={canEdit} section="money" />
                      </div>
                    </div>
                  ) : isNotesSection ? (
                    <div className="divide-y divide-border-2">
                      {s.fields.map((f) => (
                        <div
                          key={f as string}
                          data-field={f as string}
                          className="py-2"
                        >
                          <p className="text-[13.5px] font-semibold text-text mb-1">{FIELD_LABELS[f] ?? (f as string)}</p>
                          <InlineEdit
                            entityId={entity.id}
                            field={f}
                            value={(entity[f] as string | null) ?? null}
                            canEdit={canEdit}
                            multiline
                            role={role}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="divide-y divide-border-2">
                      {/* Country + Region rows — rendered at the top of the
                          Legal Identity section only */}
                      {s.sectionKey === "legal_identity" && (
                        <CountryField
                          entityId={entity.id}
                          currentCountryId={entity.countryId}
                          currentCountry={entity.country}
                          currentRegion={entity.region}
                          canEdit={canEdit}
                        />
                      )}
                      {s.fields.map((f) => (
                        <div
                          key={f as string}
                          data-field={f as string}
                          className="flex items-center gap-[14px] py-2 group"
                        >
                          <span className="w-[148px] shrink-0 text-[13.5px] font-semibold text-text">
                            {FIELD_LABELS[f] ?? (f as string)}
                          </span>
                          <div className="flex-1 min-w-0">
                            {f === "physicalOfficeRequired" ? (
                              <SelectOrOtherEditField
                                entityId={entity.id}
                                field="physicalOfficeRequired"
                                value={(entity[f] as string | null) ?? null}
                                canEdit={canEdit}
                                options={[
                                  { value: "Yes", label: t("entities:physical_office_yes", { defaultValue: "Yes" }) },
                                  { value: "No", label: t("entities:physical_office_no", { defaultValue: "No" }) },
                                ]}
                                ariaLabel={FIELD_LABELS[f] ?? (f as string)}
                              />
                            ) : f === "physicalOfficeType" ? (
                              <SelectOrOtherEditField
                                entityId={entity.id}
                                field="physicalOfficeType"
                                value={(entity[f] as string | null) ?? null}
                                canEdit={canEdit}
                                options={[
                                  { value: "Co-working — open space", label: t("entities:physical_office_type_cowork_open", { defaultValue: "Co-working — open space" }) },
                                  { value: "Co-working — closed office", label: t("entities:physical_office_type_cowork_closed", { defaultValue: "Co-working — closed office" }) },
                                  { value: "Regular office space", label: t("entities:physical_office_type_regular", { defaultValue: "Regular office space" }) },
                                ]}
                                ariaLabel={FIELD_LABELS[f] ?? (f as string)}
                              />
                            ) : (
                              <InlineEdit
                                entityId={entity.id}
                                field={f}
                                value={(entity[f] as string | null) ?? null}
                                canEdit={canEdit}
                                role={role}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>
              </div>
            );
          })}

          {/* ── Typed fields (Dates & Duration) — after entity field sections ── */}
          <div id="dates-duration">
            <SectionCard title={t("entities:section_dates_duration")}>
              <TypedFieldsEditor entity={entity} canEdit={canEdit} section="dates" />
            </SectionCard>
          </div>

          {/* ── Country services & tax guide — after Dates & Duration ── */}
          {entity.country && (
            <div>
              <CountryServicesCard
                country={entity.country}
                entityId={entity.id}
                canEdit={canEdit}
              />
            </div>
          )}

          {/* Documents */}
          <div>
            <DocumentsSection entityId={entity.id} canManage={canEdit} />
          </div>

          {/* ── Change history — visible to ALL authenticated users ── */}
          <div>
            <SectionCard
              title={t("audit:change_history_section_title")}
              hint={changelogTotal > 0 ? String(changelogTotal) : undefined}
              bodyClassName="p-0"
            >
              {changelogLoading ? (
                <div className="p-[15px] space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="skeleton h-12" />
                  ))}
                </div>
              ) : (
                <div className="px-4 py-3">
                  <HistoryTimelineErrorBoundary>
                    <ChangelogTimeline changes={changelogItems} showEntityName={false} />
                  </HistoryTimelineErrorBoundary>
                </div>
              )}
            </SectionCard>
            {changelogHasMore && (
              <div className="text-center mt-3">
                <Button
                  variant="secondary"
                  onClick={() => setChangelogLimit((l) => l + CHANGELOG_PAGE)}
                >
                  {t("audit:load_more")}
                </Button>
              </div>
            )}
            {/* "See full history →" deep-link to the global History page */}
            <div className="mt-2 text-right">
              <Link
                to={`/history?entityId=${id}`}
                aria-label={t("audit:see_full_history")}
                className="inline-flex items-center gap-1 text-[12.5px] font-medium text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded"
              >
                {t("audit:see_full_history")}
              </Link>
            </div>
          </div>

          {/* Danger zone — canEdit only */}
          {canEdit && (
            <div>
              <SectionCard
                title={t("entities:danger_zone_title")}
                danger
              >
                <div className="flex items-center gap-[13px] flex-wrap py-1">
                  <div className="flex-1 min-w-[170px]">
                    <p className="text-[13.5px] font-bold text-red-text">
                      {t("entities:danger_zone_delete_label")}
                    </p>
                    <p className="text-[12px] text-muted mt-[2px]">
                      {t("entities:danger_zone_delete_desc")}
                    </p>
                  </div>
                  <Button
                    variant="danger-soft"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    {t("entities:danger_zone_delete_button")}
                  </Button>
                </div>
              </SectionCard>

              <DeleteEntityDialog
                entityName={entity.name}
                entityId={entity.id}
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onDeleted={() => navigate("/entities")}
              />
            </div>
          )}
        </div>
      </div>
    </div>
    </EntityHistoryFieldsProvider>
  );
}
